// Hook: useOwnKillSwitch.js
// Purpose: #133 — the caller's OWN user-scope kill-switch (polled) + an audited "resume".
//          Shared by the app-wide KillSwitchBanner and the compact KillSwitchControl in the
//          strategy header, so both surfaces show the same state and the same action.

import { useCallback, useEffect, useRef, useState } from 'react';
import { services } from '../../services/api';

export const KILL_SWITCH_POLL_MS = 20000;
export const KILL_SWITCH_DEFAULT_REASON = 'Resumed from the UI after reviewing the tripped position';

export function useOwnKillSwitch({ pollMs = KILL_SWITCH_POLL_MS } = {}) {
  const [state, setState] = useState(null);   // { active, last_audit, scope_value } | null (unknown)
  const [degraded, setDegraded] = useState(false);
  const [readError, setReadError] = useState(null);   // human-readable cause of the last failed read
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const mounted = useRef(true);

  const load = useCallback(async () => {
    try {
      const res = await services.killSwitch.getOwn();
      const data = res?.data?.data || res?.data || null;
      if (!mounted.current) return;
      setState(data);
      setDegraded(false);
      setReadError(null);
    } catch (e) {
      if (!mounted.current) return;
      //! never fail-open in the UI: a failed read keeps the last known state and flags it —
      //! and says WHY (status + server detail), so a screenshot is diagnostic.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.response?.data?.error?.message || e?.message || 'unknown';
      const url = e?.config ? `${e.config.baseURL || ''}${e.config.url || ''}` : '';
      setDegraded(true);
      setReadError(`${status ? `HTTP ${status}` : 'no response'}: ${typeof detail === 'string' ? detail : JSON.stringify(detail)}${url ? ` @ ${url}` : ''}`);
      // eslint-disable-next-line no-console
      console.warn('[KillSwitch] state read failed', status, detail, url);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    load();
    const t = setInterval(load, pollMs);
    return () => {
      mounted.current = false;
      clearInterval(t);
    };
  }, [load, pollMs]);

  const resume = useCallback(async (reason) => {
    const trimmed = (reason || '').trim();
    if (trimmed.length < 3) {
      setError('Please give a reason (at least 3 characters) — it is written to the audit log.');
      return false;
    }
    setBusy(true);
    setError(null);
    try {
      await services.killSwitch.clearOwn(trimmed);
      await load();
      return true;
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.response?.data?.error?.message || e?.message;
      setError(`Could not resume: ${detail || 'unknown error'}`);
      return false;
    } finally {
      if (mounted.current) setBusy(false);
    }
  }, [load]);

  const trip = state?.last_audit?.action === 'SET' ? state.last_audit : null;

  return { state, active: Boolean(state?.active), trip, degraded, readError, busy, error, setError, resume, reload: load };
}

export const fmtIstTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: false });
  } catch {
    return iso;
  }
};
